import { useMemo, useState, type ReactNode } from 'react';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { SUPPLIER_TRADE_LABELS, billOverdue, nzDate, parseRef } from '@promanage/core';
import { Button, Amount } from '@promanage/ui';
import { ColumnPicker } from '../features/table/ColumnPicker';
import { DataTable } from '../features/table/DataTable';
import { EmptyState, FilterChips, ListShell } from '../features/table/ListShell';
import { compareRows, type SortState } from '../features/table/columns';
import { useColumnState } from '../features/table/useColumnState';
import { BILL_COLUMNS, DEFAULT_BILL_SORT, type BillColumnId } from '../features/bills/billColumns';
import type { BillRow } from '../features/bills/types';
import { BillsNav, type BillsTab } from '../features/bills/BillsNav';
import { TenancyInvoicesList } from '../features/tenancyInvoices/TenancyInvoicesList';
import { BillInboxTab } from '../features/bills/BillInbox';
import { trpc } from '../lib/trpc';
import { WorkspaceMenu, useWorkspace } from '../features/workspaces/WorkspaceMenu';
import { workspaceChoice, workspaceTable, workspaceText } from '../features/workspaces/state';

export const Route = createFileRoute('/bills/')({
  /*
   * INLINE AND IMPORT-FREE, for `tabSearch.ts`'s reason: `validateSearch` runs during routing, so
   * it is one of the few things `autoCodeSplitting` cannot lift out of this file, and anything it
   * reached would land in the eager bundle. Anything unrecognised is the supplier tab — a
   * mistyped `?tab=` opens the page where it always opens rather than refusing to render.
   */
  validateSearch: (search: Record<string, unknown>): { tab?: 'tenancy' | 'inbox' } =>
    search.tab === 'tenancy' || search.tab === 'inbox' ? { tab: search.tab } : {},
  component: BillsPage,
});

/**
 * Which half of the page is open. Two components rather than one with a branch in it, so each
 * tab's hooks — its query, its filters, its saved columns — only run while it is on screen.
 */
function BillsPage() {
  const { tab } = Route.useSearch();
  const current: BillsTab = tab ?? 'supplier';
  const nav = <BillsNav current={current} />;

  if (current === 'tenancy') return <TenancyInvoicesList nav={nav} />;
  if (current === 'inbox') return <BillInboxTab nav={nav} />;
  return <SupplierBills nav={nav} />;
}

const STORAGE_KEY = 'promanage:bill-columns';

/** One identity, so the pending state does not re-sort the table on every render. */
const NO_BILLS: BillRow[] = [];

type BillFilter = 'open' | 'awaiting' | 'approved' | 'overdue' | 'noInvoice' | 'all';

const FILTERS: { value: BillFilter; label: string }[] = [
  { value: 'open', label: 'Open' },
  { value: 'awaiting', label: 'Awaiting approval' },
  { value: 'approved', label: 'Approved, unpaid' },
  { value: 'overdue', label: 'Overdue' },
  { value: 'noInvoice', label: 'No invoice' },
  { value: 'all', label: 'All' },
];

/** What an empty list says under each filter — a sentence per filter, not a label bent to fit. */
const NOTHING: Record<BillFilter, string> = {
  open: 'Nothing waiting',
  awaiting: 'No bills awaiting approval',
  approved: 'No bills approved and unpaid',
  overdue: 'Nothing overdue',
  noInvoice: 'Every bill has its invoice attached',
  all: 'No bills yet',
};

/**
 * Bills — what the agency has been invoiced, and what it has done about it.
 *
 * THE DEFAULT FILTER IS "OPEN", not "All", and that is the difference between a queue and an
 * archive. Every bill an agency has ever paid stays on this table for ever; what somebody sitting
 * down at it on a Tuesday morning needs is the handful nobody has dealt with. Paid and rejected
 * bills are one click away and never the first thing on the screen.
 *
 * AND ONE TAB ACROSS, WHAT THE TENANTS OWE. A supplier bill and a tenancy invoice are the two
 * halves of one routine act — the water bill arrives and the owner is out of pocket, the tenant is
 * liable under the agreement and a charge goes back the other way. The person working this pile is
 * the one who knows which bills are recoverable; see `BillsNav` for why it is a tab.
 *
 * THE TWO NUMBERS ABOVE THE TABLE ARE OBLIGATIONS, not turnover. "Waiting on approval" is money
 * the owners still have and might be about to lose; "approved, unpaid" is money they have already
 * lost the use of and the supplier has not yet received. The second of those is the balance of the
 * supplier control account (TRUST_ACCOUNTING.md §2) and is the figure that has to reconcile.
 */
function SupplierBills({ nav }: { nav: ReactNode }) {
  const navigate = useNavigate();
  const list = useQuery(
    trpc.bills.list.queryOptions(undefined, {
      meta: { errorTitle: 'Could not load the bills' },
      staleTime: 10_000,
    }),
  );
  const bills = list.data ?? NO_BILLS;

  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState<BillFilter>('open');
  const [sort, setSort] = useState<SortState<BillColumnId>>(DEFAULT_BILL_SORT);
  const columns = useColumnState(STORAGE_KEY, BILL_COLUMNS);
  const workspace = useWorkspace({
    route: '/bills',
    state: { filters: { query, filter }, columns: columns.state.order, sort },
    apply(state) {
      const table = workspaceTable(state, BILL_COLUMNS, DEFAULT_BILL_SORT);
      setQuery(workspaceText(state, 'query'));
      setFilter(
        workspaceChoice(
          state,
          'filter',
          FILTERS.map((option) => option.value),
          'open',
        ),
      );
      columns.setState(table.columns);
      setSort(table.sort);
    },
  });

  const today = nzDate(new Date());

  const totals = useMemo(() => {
    let awaiting = 0n;
    let approved = 0n;
    for (const bill of bills) {
      if (bill.state === 'awaiting_approval') awaiting += BigInt(bill.amountCents);
      // The approved figure is what has been committed and not yet sent — the same thing the
      // supplier control account holds, arrived at from the queue rather than from the ledger.
      if (bill.state === 'approved') approved += BigInt(bill.amountCents) - BigInt(bill.paidCents);
    }
    return { awaiting, approved };
  }, [bills]);

  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    return bills
      .filter((bill) => {
        if (filter === 'all') return true;
        if (filter === 'awaiting') return bill.state === 'awaiting_approval';
        if (filter === 'approved') return bill.state === 'approved';
        if (filter === 'overdue') {
          // Only what somebody still owes — a paid bill that went out late is history.
          const chaseable = bill.state === 'awaiting_approval' || bill.state === 'approved';
          return chaseable && billOverdue(bill.dueOn, today);
        }
        // Paper missing from anything still standing — a rejected bill was never going to be
        // paid, so there is nothing for its invoice to be checked against.
        if (filter === 'noInvoice') return !bill.hasInvoice && bill.state !== 'rejected';
        return bill.state === 'awaiting_approval' || bill.state === 'approved';
      })
      .filter((bill) => {
        if (!q) return true;

        const asRef = parseRef(q);
        if (asRef) {
          if (asRef.entity === 'bill') return asRef.sequence === bill.refSeq;
          // A supplier or property ref pasted into the box is a reasonable way to ask "everything
          // for this contractor" without leaving the page.
          if (asRef.entity === 'supplier') return bill.supplierRef.toLowerCase() === q;
          if (asRef.entity === 'property') return bill.propertyRef.toLowerCase() === q;
          return false;
        }

        return [
          bill.ref,
          bill.supplierName,
          SUPPLIER_TRADE_LABELS[bill.supplierTrade],
          bill.propertyAddress,
          bill.description,
          bill.invoiceNumber,
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase()
          .includes(q);
      })
      .sort((a, b) => compareRows(a, b, sort, BILL_COLUMNS));
  }, [bills, query, filter, sort, today]);

  const total = bills.length;
  const summary = list.isPending
    ? 'Loading…'
    : rows.length === total
      ? `${total} ${total === 1 ? 'bill' : 'bills'}`
      : `Showing ${rows.length} of ${total} bills`;

  const obligations =
    totals.awaiting > 0n || totals.approved > 0n ? (
      <>
        {summary} · <Amount value={totals.awaiting} currency tone="neutral" /> waiting on approval ·{' '}
        <Amount value={totals.approved} currency tone="neutral" /> approved and unpaid
      </>
    ) : (
      summary
    );

  return (
    <ListShell
      title="Bills"
      subtitle={list.isPending ? summary : obligations}
      nav={nav}
      actions={
        <>
          <WorkspaceMenu workspace={workspace} />
          <ColumnPicker
            columns={BILL_COLUMNS}
            state={columns.state}
            onChange={columns.setState}
            onReset={columns.reset}
          />
          <Link to="/bills/new">
            <Button size="sm">
              <span aria-hidden="true">+</span> Add bill
            </Button>
          </Link>
        </>
      }
      search={query}
      onSearchChange={setQuery}
      searchPlaceholder="Search contractor, address, work, invoice number or ref"
      searchLabel="Search bills"
      filters={
        <FilterChips label="Filter bills" value={filter} options={FILTERS} onChange={setFilter} />
      }
      isPending={list.isPending}
      error={list.error}
      onRetry={() => void list.refetch()}
      isEmpty={rows.length === 0}
      empty={
        <EmptyState
          title={query ? `Nothing matches “${query}”` : NOTHING[filter]}
          action={
            <Link to="/bills/new">
              <Button size="sm">Add bill</Button>
            </Link>
          }
        >
          {query
            ? 'Try a different contractor, address, invoice number or ref.'
            : 'A bill is one invoice, from one contractor, about one property. Approving it charges the owners of that property; paying it sends the money out of the trust account.'}
        </EmptyState>
      }
    >
      <DataTable
        rows={rows}
        columns={columns.visible}
        sort={sort}
        onSortChange={setSort}
        rowKey={(bill) => bill.id}
        onOpen={(bill) => void navigate({ to: '/bills/$ref', params: { ref: bill.ref } })}
        caption={`Bills, one row per contractor invoice. ${summary}.`}
        minWidth="64rem"
      />
    </ListShell>
  );
}
